import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Brain, AlertTriangle, Shield, TrendingUp, BarChart3, Clock, CheckCircle2, XCircle, Users, Home, Activity } from 'lucide-react';

const RISK_COLORS = { CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-red-100 text-red-800', MEDIUM: 'bg-amber-100 text-amber-800', LOW: 'bg-gray-100 text-gray-700' };
const FLAG_ICONS = { REPEAT_PARTICIPANT_RISK: Users, HIGH_RISK_ACCOMMODATION: Home, STAFF_PATTERN_ALERT: Users, OVERDUE_INCIDENT: Clock, POTENTIAL_REPORTABLE_INCIDENT: Shield, COMPLIANCE_DELAY: AlertTriangle };

export default function IncidentIntelligencePage() {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [riskFlags, setRiskFlags] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      api.get('/incidents/intelligence/analytics'),
      api.get('/incidents/intelligence/trends?days=90'),
      api.get('/incidents/intelligence/risk-flags'),
      api.get('/incidents/intelligence/compliance-alerts'),
    ]).then(([aR, tR, rR, cR]) => {
      if (aR.status === 'fulfilled') setAnalytics(aR.value.data.data);
      if (tR.status === 'fulfilled') setTrends(tR.value.data.data?.trends || []);
      if (rR.status === 'fulfilled') setRiskFlags(rR.value.data.data?.flags || []);
      if (cR.status === 'fulfilled') setComplianceAlerts(cR.value.data.data?.alerts || []);
    });
  }, []);

  const o = analytics?.overview || {};
  const sd = analytics?.severity_distribution || {};
  const std = analytics?.status_distribution || {};
  const td = analytics?.type_breakdown || {};
  const ca = analytics?.compliance_alerts || {};

  return (
    <div className="space-y-6" data-testid="incident-intelligence-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/incidents')}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Brain className="w-6 h-6 text-primary" />Incident Intelligence</h1>
            <p className="text-muted-foreground mt-0.5">AI-Powered Risk Analytics & Compliance Monitoring</p>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Total', value: o.total, icon: BarChart3, color: '' },
          { label: 'Open', value: o.open, icon: Clock, color: 'text-blue-600' },
          { label: 'Resolved', value: o.resolved, icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Closed', value: o.closed, icon: CheckCircle2, color: '' },
          { label: 'High', value: o.high_severity, icon: AlertTriangle, color: 'text-red-500' },
          { label: 'Critical', value: o.critical, icon: XCircle, color: 'text-red-700' },
          { label: 'This Week', value: o.this_week, icon: Activity, color: 'text-blue-600' },
          { label: 'Reportable', value: o.reportable, icon: Shield, color: 'text-amber-600' },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <Card key={i}>
              <CardContent className="py-3 text-center">
                <Icon className={`w-4 h-4 mx-auto mb-1 ${c.color || 'text-muted-foreground'}`} />
                <p className="text-2xl font-bold">{c.value || 0}</p>
                <p className="text-[10px] text-muted-foreground">{c.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="risk">
        <TabsList>
          <TabsTrigger value="risk" data-testid="tab-risk"><AlertTriangle className="w-4 h-4 mr-1" />Risk Flags ({riskFlags.length})</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance"><Shield className="w-4 h-4 mr-1" />Compliance ({complianceAlerts.length})</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="tab-distribution"><BarChart3 className="w-4 h-4 mr-1" />Distribution</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends"><TrendingUp className="w-4 h-4 mr-1" />Trends</TabsTrigger>
        </TabsList>

        {/* Risk Flags Tab */}
        <TabsContent value="risk" className="mt-4 space-y-3">
          {riskFlags.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground"><CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" /><p className="font-medium">No risk flags detected</p><p className="text-sm mt-1">All incident patterns are within normal parameters</p></CardContent></Card>
          ) : riskFlags.map((f, i) => {
            const Icon = FLAG_ICONS[f.flag_type] || AlertTriangle;
            return (
              <Card key={i} className={f.risk_level === 'CRITICAL' ? 'border-red-300' : f.risk_level === 'HIGH' ? 'border-red-200' : ''} data-testid={`risk-flag-${i}`}>
                <CardContent className="py-3 flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${RISK_COLORS[f.risk_level]?.split(' ')[0] || 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${f.risk_level === 'CRITICAL' ? 'text-white' : ''}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge className={RISK_COLORS[f.risk_level]}>{f.risk_level}</Badge>
                      <Badge variant="outline" className="text-[10px]">{f.flag_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    <p className="text-sm font-medium">{f.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{f.entity_type}: {f.entity_name} · {f.incident_count} incident(s) · {f.period}</p>
                  </div>
                  {f.entity_type === 'incident' && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/compliance/incidents/${f.entity_id}`)}>View</Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Compliance Alerts Tab */}
        <TabsContent value="compliance" className="mt-4 space-y-3">
          {complianceAlerts.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground"><CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" /><p>No compliance alerts</p></CardContent></Card>
          ) : complianceAlerts.map((a, i) => (
            <Card key={i} className={a.severity === 'CRITICAL' ? 'border-red-300 bg-red-50' : a.severity === 'HIGH' ? 'border-amber-200' : ''} data-testid={`compliance-alert-${i}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={RISK_COLORS[a.severity]}>{a.severity}</Badge>
                  <div>
                    <p className="text-sm font-medium">{a.message}</p>
                    <p className="text-xs text-muted-foreground">{a.type.replace(/_/g, ' ')} · {a.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
                {a.incident_id && <Button variant="outline" size="sm" onClick={() => navigate(`/compliance/incidents/${a.incident_id}`)}>View</Button>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Distribution Tab */}
        <TabsContent value="distribution" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Severity */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Severity</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(sev => {
                    const count = sd[sev] || 0;
                    const total = Object.values(sd).reduce((a, b) => a + b, 0) || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={sev} className="flex items-center gap-3">
                        <span className="text-xs w-16 text-muted-foreground">{sev}</span>
                        <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${sev === 'LOW' ? 'bg-gray-400' : sev === 'MEDIUM' ? 'bg-amber-400' : sev === 'HIGH' ? 'bg-red-400' : 'bg-red-700'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-sm font-bold w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Status */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Status</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(std).filter(([, v]) => v > 0).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between py-1">
                      <span className="text-xs text-muted-foreground">{status}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                  ))}
                  {Object.values(std).every(v => v === 0) && <p className="text-sm text-muted-foreground text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>

            {/* Type */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Incident Types</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(td).sort(([, a], [, b]) => b - a).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between py-1">
                      <span className="text-xs text-muted-foreground">{type.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                  ))}
                  {Object.keys(td).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No data</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Weekly Incident Trends (Last 90 Days)</CardTitle></CardHeader>
            <CardContent>
              {trends.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No trend data available</p>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground pb-2 border-b">
                    <span className="w-24">Week</span>
                    <span className="flex-1">Total</span>
                    <span className="w-20 text-right">High/Critical</span>
                    <span className="w-20 text-right">Reportable</span>
                  </div>
                  {trends.map((t, i) => {
                    const maxTotal = Math.max(...trends.map(x => x.total)) || 1;
                    return (
                      <div key={i} className="flex items-center gap-4 py-1.5">
                        <span className="text-xs font-mono w-24">{t.week}</span>
                        <div className="flex-1 flex items-center gap-2">
                          <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary/60 rounded-full" style={{ width: `${(t.total / maxTotal) * 100}%` }} />
                          </div>
                          <span className="text-sm font-bold w-6">{t.total}</span>
                        </div>
                        <span className={`text-sm w-20 text-right ${t.high_critical > 0 ? 'text-red-600 font-bold' : 'text-muted-foreground'}`}>{t.high_critical}</span>
                        <span className={`text-sm w-20 text-right ${t.reportable > 0 ? 'text-amber-600 font-bold' : 'text-muted-foreground'}`}>{t.reportable}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
